import { useState, forwardRef, useImperativeHandle, useRef } from 'react'
import styles from '@styles/components/TextInput.module.css'

// Define custom ref type
export interface TextInputRef {
    focus: () => void
}

interface TextInputProps {
    label: string
    value: string
    onChange: (value: string) => void
    placeholder?: string
    type?: 'text' | 'password' | 'tel' | 'email' | 'number'
    inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal' | 'search' | 'url'
    error?: string
    loading?: boolean
    onEnterPress?: () => void
    onEnterFocus?: () => void
    onLostFocus?: () => void
    width?: string
    icon?: React.ReactNode
}

export const TextInput = forwardRef<TextInputRef, TextInputProps>(({ 
    label, value, onChange, placeholder, error, loading = false, onEnterPress = ()=>{},
    width, icon, onEnterFocus = ()=>{}, onLostFocus = ()=>{}, type = 'text', inputMode
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus()
        }
    }))

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (onEnterPress) onEnterPress()
        }
    }

    return (
        <div 
            className={styles['main-container']}
            style={{
                width: width
            }}
        >
            {label && (
                <div className={styles['input-label-container']}>
                    <p className={styles['input-label']}>{label}</p>
                </div>
            )}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                position: 'relative',
                alignItems: 'center',
                alignContent: 'start',
            }}>
                {icon && (
                    <div style={{
                        position: 'absolute',
                        left: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}>
                        {icon}
                    </div>
                )}                
                <input
                    ref={inputRef}
                    className={`${styles['input-container']} ${loading ? styles.skeleton : ''}`}
                    type={type}
                    inputMode={inputMode}
                    placeholder={!loading && placeholder}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={onEnterFocus}
                    onBlur={onLostFocus}
                    value={value}
                    disabled={loading}
                    style={{
                        paddingLeft: icon ? '48px' : undefined,
                        width: icon ? `calc(100% - 48px)` : '100%'
                    }}
                />
            </div>
            {error && <span className={styles['error-message']}>{error}</span>}
        </div>
    )
})
// Add display name - THIS IS CRITICAL!
TextInput.displayName = 'TextInput'

export const TextAreaInput = forwardRef<TextInputRef, TextInputProps>(({ 
    label, value, onChange, placeholder, error, loading = false, onEnterPress = ()=>{},
    onEnterFocus = ()=>{}, onLostFocus = ()=>{}
}, ref) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => ({
        focus: () => {
            textAreaRef.current?.focus()
        }
    }))

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
         if (e.key === 'Enter' && !e.shiftKey && onEnterPress) {
            e.preventDefault()
            onEnterPress()
        }
    }
    
    return (
        <div className={styles['main-container']}>
            <div className={styles['input-label-container']}>
                <p className={styles['input-label']}>{label}</p>
            </div>
            <textarea
                ref={textAreaRef}
                className={`${styles['input-container']} ${loading ? styles.skeleton : ''}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={onEnterFocus}
                onBlur={onLostFocus}
                placeholder={!loading && placeholder}
                disabled={loading}
            />
            {error && <span className={styles['error-message']}>{error}</span>}
        </div>
    )
})

// Add display name - THIS IS CRITICAL!
TextAreaInput.displayName = 'TextAreaInput'